/**
 * Proposition 65 warning — California visitor gate.
 *
 * The warning is rendered server-side and is VISIBLE by default. This script only ever
 * HIDES it, and only on a confirmed non-California result. Every failure mode (network
 * error, timeout, blocked request, missing edge data) leaves the warning visible, which
 * is the compliance-safe direction.
 *
 * Region comes from /api/geo-region, served by the Cloudflare worker from request.cf.
 * Note Cloudflare uses "CA" for both California (regionCode) and Canada (country), so
 * country must always be checked alongside region.
 */
(function () {
  var STORAGE_KEY = 'theme:geo-region';
  // Served by the Cloudflare Worker on its own hostname: the apex is proxied to Shopify's
  // Cloudflare-hosted edge (orange-to-orange), where zone Worker routes do not run.
  var ENDPOINT = 'https://edge.hydromateusa.com/api/geo-region';
  var TTL = 24 * 60 * 60 * 1000;
  var TIMEOUT = 2000;

  if (!document.querySelector('[data-prop-65]')) {
    return;
  }

  // Merchants must always see the block while editing, wherever they happen to be.
  if (window.Shopify && window.Shopify.designMode) {
    return;
  }

  function apply(verdict, fromNetwork) {
    if (verdict === 'hide' && fromNetwork && isInViewport()) {
      // The live verdict landed after the row already painted. Cumulative Layout Shift
      // only counts shifts inside the viewport, so leave it alone this once; the cached
      // verdict hides it pre-paint on the next page view.
      return;
    }

    document.documentElement.classList.toggle('geo-not-ca', verdict === 'hide');
  }

  function isInViewport() {
    var el = document.querySelector('[data-prop-65]');

    if (!el) {
      return false;
    }

    var rect = el.getBoundingClientRect();
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    return rect.bottom > 0 && rect.top < viewportHeight;
  }

  function verdictFor(data) {
    if (!data || !data.country) {
      return 'show'; // Unknown location — fail open.
    }

    if (data.country !== 'US') {
      return 'hide'; // Confirmed outside the US, so confirmed outside California.
    }

    if (!data.region) {
      return 'show'; // US, but state is ambiguous — fail open.
    }

    return data.region === 'CA' ? 'show' : 'hide';
  }

  function readCache() {
    try {
      var cached = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');

      if (cached && typeof cached.ts === 'number' && Date.now() - cached.ts < TTL) {
        return cached.verdict;
      }
    } catch (e) {
      // Safari in private mode does not allow reading item, we silently fail
    }

    return null;
  }

  function writeCache(verdict) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ verdict: verdict, ts: Date.now() }));
    } catch (e) {
      // Safari in private mode does not allow setting item, we silently fail
    }
  }

  // QA override: ?geo_region=TX to verify hiding, ?geo_region=CA to force the warning.
  var override = new URLSearchParams(window.location.search).get('geo_region');

  if (override) {
    apply(verdictFor({ country: 'US', region: override.toUpperCase() }));
    return;
  }

  var cachedVerdict = readCache();

  if (cachedVerdict) {
    apply(cachedVerdict);
    return;
  }

  var controller = 'AbortController' in window ? new AbortController() : null;
  var timer = controller
    ? setTimeout(function () {
        controller.abort();
      }, TIMEOUT)
    : null;

  fetch(ENDPOINT, {
    credentials: 'omit',
    signal: controller ? controller.signal : undefined,
  })
    .then(function (response) {
      return response.ok ? response.json() : null;
    })
    .then(function (data) {
      var verdict = verdictFor(data);
      writeCache(verdict);
      apply(verdict, true);
    })
    .catch(function () {
      // Fail open: the warning stays visible and no verdict is cached, so the next
      // page view retries rather than locking in a failure.
    })
    .then(function () {
      if (timer) {
        clearTimeout(timer);
      }
    });
})();
